import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../main/AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import WhatsAppNotice from '../../components/common/WhatsAppNotice';
import StorefrontHelp from './StorefrontHelp';
import { apiRequest } from '../../utils';
import { readCart, clearCart, computeTotals } from '../shop/cart';
import './Storefront.css';

/* Storefront checkout (/:slug/checkout/:eventId) — guest or logged in.
   Two shapes: the event-scoped cart (mode 'full', whole packages) or
   ?mode=first_seat&rate=<id> (only your own seat of a team rate; a fresh team
   with you as captain).
   A guest is never asked to log in, whether or not the address they typed is
   already in our database — it usually is, because our own CRM put it there, and
   asking a buyer for a password they never had is how you lose the order. The
   server files the order on the person who exists and simply hands out no session.
   Paying: "Pay by card" (Stripe) or, where the brand allows it, "Send me an
   invoice" — which writes nothing until the buyer opens the link we mail them.
   Backend: POST /api/storefront/checkout. */

const Field = ({ label, children }) => (
  <div className="auth__field">
    <label className="auth__label">{label}</label>
    <div className="auth__input-wrap">{children}</div>
  </div>
);

const money = (currency, value) => `${currency} ${Number(value).toFixed(2)}`;

const StorefrontCheckout = () => {
  const { t } = useTranslation();
  const { slug, eventId } = useParams();
  const navigate = useNavigate();
  // The global usePersistUrlParams hook moves query params into sessionStorage
  // and strips them from the URL, so read the query first and fall back there.
  const query = new URLSearchParams(useLocation().search);
  const mode = (query.get('sf_mode') || sessionStorage.getItem('sf_mode')) === 'first_seat'
    ? 'first_seat' : 'full';
  const seatRateId = query.get('sf_rate') || sessionStorage.getItem('sf_rate');
  // Referral attribution (Phase-1): a team's cheer link lands here as ?ref_team=.
  const refTeam = query.get('ref_team') || sessionStorage.getItem('ref_team') || '';
  // A community leader's code, carried from her booking link. It does double duty: it
  // unlocks her community's not-public rate in the catalog AND attributes the booking, so
  // it is pre-filled into the voucher field rather than sent as a separate flag.
  const communityCode = query.get('sf_vc') || sessionStorage.getItem('sf_vc') || '';

  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Asked for an invoice: the order is waiting on the confirm link we just mailed.
  // Holds the address it went to, which is the whole content of that screen.
  const [pendingEmail, setPendingEmail] = useState(null);
  // A team of the same name is already playing here (409 teamNameExists). Holds
  // the server's answer — the existing team and who started it — until the buyer
  // says whether she wants to join that one or really needs a separate team.
  const [nameClash, setNameClash] = useState(null);
  // The edition's team cap does not leave room for this basket (409 teamCapReached). Holds
  // the server's answer — the spots still left and the waiting list — for its own card.
  const [capFull, setCapFull] = useState(null);
  const confirmNewTeam = useRef(false);
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem('isLoggedIn') === '1');
  const [sessionUser, setSessionUser] = useState(null);
  // Signed in, but no number on the profile: still ask for it (the checkout is the one
  // moment we can get it; hiding the field left those buyers without one for good).
  const askMobile = loggedIn && sessionUser != null && sessionUser.has_mobile === false;
  // A Bravos member with Lab tickets left books this Lab with one instead of paying.
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const [idempotencyKey] = useState(() =>
    (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', mobile: '', team_name: '',
    street: '', number: '', zip: '', city: '', country: '',
    company_order: false, company_name: '', company_vat: '',
    voucher_code: communityCode,
    terms_accepted: false,
  });
  // Missing-required marking: some browsers (and autofill quirks) let a form
  // submit past native `required`, and a bare server error is terrible UX —
  // so validate ourselves and paint the offending fields red.
  const [missing, setMissing] = useState(new Set());
  const cls = (k) => 'auth__input' + (missing.has(k) ? ' storefront__input--missing' : '');
  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (missing.has(key) && value) {
      setMissing((m) => { const n = new Set(m); n.delete(key); return n; });
    }
  };

  // Applied-voucher preview (UX only — the order re-validates + re-applies at checkout).
  const [voucher, setVoucher] = useState(null);          // {code, discount, discount_type}
  const [voucherError, setVoucherError] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);

  const cart = useMemo(() => readCart(eventId), [eventId]);

  // What the B2B block calls the company tax id. The BU behind the storefront decides:
  // herculestrophyuae bills under the UAE regime (TRN), herculestrophybe under the
  // Belgian one (VAT number). Same field, same column — only the wording moves.
  const isTrn = catalog && catalog.tax_id_kind === 'trn';
  const taxIdLabel = isTrn ? t('companyTrn') : t('companyVat');
  const taxIdPlaceholder = isTrn ? t('companyTrnPlaceholder') : t('companyVatPlaceholder');

  useEffect(() => {
    (async () => {
      try {
        // Carry her community code: without it the catalog here does NOT contain the
        // not-public community rate, so the seat/cart line she came to buy would silently
        // vanish from this page — and `community` (the "booking with X" line) would be null.
        setCatalog(await apiRequest(
          'GET',
          `/storefront/event/${eventId}`
          + (communityCode ? `?vc=${encodeURIComponent(communityCode)}` : '')));
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, communityCode, t]);

  // HT brands (Hercules Trophy) sell to companies, so "I need a company invoice"
  // starts ticked there — it is the normal case, not the exception. Only ONCE, when
  // the catalog first lands: after that the box is the buyer's, and un-ticking it
  // must stick. Nothing else changes — the same fields, the same validation.
  const companyDefaulted = useRef(false);
  useEffect(() => {
    if (!catalog || companyDefaulted.current) return;
    companyDefaulted.current = true;
    if (catalog.business_model === 'HT') {
      setForm((f) => ({ ...f, company_order: true }));
    }
  }, [catalog]);

  // The localStorage flag outlives the JWT cookie — ask the SERVER whether we
  // are really logged in, or a stale flag hides the email/name fields from an
  // anonymous guest.
  useEffect(() => {
    (async () => {
      try {
        const s = await apiRequest('GET', '/storefront/session');
        setLoggedIn(Boolean(s.logged_in));
        setSessionUser(s.logged_in ? s : null);
        if (s.logged_in) {
          localStorage.setItem('isLoggedIn', '1');
        } else if (localStorage.getItem('isLoggedIn') === '1') {
          localStorage.removeItem('isLoggedIn');
          window.dispatchEvent(new Event('auth-changed'));
        }
      } catch { /* keep the optimistic default */ }
    })();
  }, []);

  const logout = async () => {
    try { await apiRequest('POST', '/logout'); } catch { /* cookie may be gone */ }
    localStorage.removeItem('isLoggedIn');
    window.dispatchEvent(new Event('auth-changed'));
    setLoggedIn(false);
    setSessionUser(null);
  };

  const accent = catalog && catalog.primary_color
    ? { backgroundColor: catalog.primary_color } : undefined;
  // Scope --primary-color to the event's brand accent for this card only. The
  // Bootstrap checkbox (Bootstrap.css paints its :checked state with that variable)
  // and the terms link then match the buttons, which set the colour inline.
  const cardStyle = catalog && catalog.primary_color
    ? { '--primary-color': catalog.primary_color } : undefined;
  const seatProduct = catalog && seatRateId
    ? catalog.products.find((p) => p.rate_id === seatRateId)
    : null;
  // A solo ticket (Meet the Ladies): the product decides, the server derives it the same way.
  const solo = mode === 'first_seat' && !!seatProduct?.is_solo_product;
  const cartLines = catalog
    ? catalog.products.filter((p) => Number(cart[p.rate_id]) > 0)
    : [];
  // Booking with a community leader's code. She already exists (created in the back
  // office), so nothing here creates or edits a community — this is an ordinary
  // personal booking that we simply label. `catalog.community` covers arriving on her
  // link (?sf_vc=), which is also what reveals her hidden rate; the previewed voucher
  // covers someone who simply TYPED the code, which attributes the booking just the same
  // and used to leave this line off.
  const community = catalog?.community || voucher?.community || null;
  const totals = catalog ? computeTotals(catalog.products, cart, catalog.with_vat) : null;
  const empty = mode === 'full' ? cartLines.length === 0 : !seatProduct;
  // Full cart, voucher-aware: the discount is NET and the checkout charges VAT on the
  // discounted lines, so the VAT on the discount (discount_vat) falls away with it — a free
  // code must not leave the VAT standing.
  const cartDiscount = voucher ? Number(voucher.discount) || 0 : 0;
  const cartVat = totals
    ? Math.max(totals.vat - (voucher && catalog.with_vat ? Number(voucher.discount_vat) || 0 : 0), 0)
    : 0;
  const cartTotal = totals ? Math.max(totals.subtotal - cartDiscount, 0) + cartVat : 0;
  // first_seat price breakdown, voucher-aware: the discount is on the NET seat price and VAT
  // is computed on the discounted net — mirrors what /api/storefront/checkout charges.
  const seatNet = seatProduct ? Number(seatProduct.seat_price) || 0 : 0;
  const seatDiscount = voucher ? Math.min(Number(voucher.discount) || 0, seatNet) : 0;
  const seatNetAfter = Math.max(seatNet - seatDiscount, 0);
  const seatVatRate = seatProduct ? Number(seatProduct.vat_rate) || 0 : 0;

  // Clearing/editing the code drops a stale preview so the total can't lie.
  const onVoucherChange = (e) => {
    set('voucher_code')(e);
    if (voucher || voucherError) { setVoucher(null); setVoucherError(null); }
  };

  const applyVoucher = async () => {
    const code = form.voucher_code.trim();
    if (!code || voucherLoading) return;
    setVoucherLoading(true);
    setVoucherError(null);
    try {
      const res = await apiRequest('POST', '/storefront/voucher-preview', {
        event_id: eventId,
        voucher_code: code,
        mode,
        basket: mode === 'first_seat'
          ? [{ rate_id: seatRateId, qty: 1 }]
          : cartLines.map((p) => ({ rate_id: p.rate_id, qty: Number(cart[p.rate_id]) })),
      });
      if (res.ok) { setVoucher(res); setVoucherError(null); }
      // The server names the real reason; `voucher_limit` makes "already used" say how often.
      else { setVoucher(null); setVoucherError({ key: res.error || 'voucherInvalid', count: res.voucher_limit }); }
    } catch (err) {
      setVoucher(null);
      setVoucherError({ key: err.message || 'voucherInvalid', count: err.data?.voucher_limit });
    } finally {
      setVoucherLoading(false);
    }
  };

  // The storefront used to sell a seat to a member holding ten paid ones (ORDBRVS_0080).
  // Same endpoint the member home's Register button uses: own pool first, then the company's.
  const spendTicket = () => {
    if (claiming) return;
    setClaiming(true);
    apiRequest('POST', '/bravos/claim-seat', { event_id: eventId })
      .then(() => { if (mode === 'full') clearCart(eventId); setClaimed(true); })
      .catch((err) => {
        setError(t(err.message, { defaultValue: t('claimFailed') }));
        setShowToast(true);
      })
      .finally(() => setClaiming(false));
  };

  const buy = async (e, paymentMethod = 'card') => {
    if (e && e.preventDefault) e.preventDefault();
    const requiredKeys = [];
    if (!loggedIn) requiredKeys.push('first_name', 'last_name', 'email', 'mobile');
    else if (askMobile) requiredKeys.push('mobile');
    if (form.company_order) requiredKeys.push('company_name', 'company_vat', 'street', 'zip', 'city', 'country');
    const miss = new Set(requiredKeys.filter((k) => !String(form[k] || '').trim()));
    if (catalog.terms_url && !form.terms_accepted) miss.add('terms_accepted');
    if (miss.size) {
      setMissing(miss);
      setError(t('fillRequiredFields'));
      setShowToast(true);
      return;
    }
    setSubmitting(true);
    try {
      const basket = mode === 'first_seat'
        ? [{ rate_id: seatRateId, qty: 1 }]
        : cartLines.map((p) => ({ rate_id: p.rate_id, qty: Number(cart[p.rate_id]) }));
      const data = await apiRequest('POST', '/storefront/checkout', {
        event_id: eventId, mode, basket, idempotency_key: idempotencyKey, slug, ...form,
        ref_team: refTeam,
        payment_method: paymentMethod,
        // Set only after the buyer has seen the namesake team and said it is a
        // different one — otherwise the server refuses to make the twin.
        confirm_new_team: confirmNewTeam.current,
      });
      // Invoice, from a guest: the order and its proposal exist, the invoice waits
      // on the link we just mailed. The cart is spent either way — leaving it full
      // is how the same order gets placed twice.
      if (data.pending_confirmation) {
        if (mode === 'full') clearCart(eventId);
        setPendingEmail(data.email || form.email);
        return;
      }
      if (data.new_account) {
        localStorage.setItem('isLoggedIn', '1');
        window.dispatchEvent(new Event('auth-changed'));
      }
      if (data.checkout_url) {
        // Online payment: pay on Stripe's hosted page first. Keep the cart until
        // we come back confirmed-paid (an abandoned payment must not empty it).
        window.location.href = data.checkout_url;
        return;
      }
      if (mode === 'full') clearCart(eventId);
      navigate(`/${slug}/confirmation`, {
        state: { result: data, slug, color: catalog.primary_color, eventId },
      });
    } catch (err) {
      // A team of that name is already playing here. Not an error to shout at
      // them — a question: join hers, or really make a second one?
      if (err.message === 'teamNameExists') {
        setNameClash(err.data || {});
        return;
      }
      if (err.message === 'teamCapReached') {
        setCapFull(err.data || {});
        return;
      }
      setError(t(err.message, { count: err.data?.voucher_limit, defaultValue: err.message }));
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('checkout')}
      subtitle={catalog ? catalog.event.name : ''}
      wide
    >
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !catalog ? (
        <div className="auth__card"><p>{error || t('storefrontNotFound')}</p></div>
      ) : claimed ? (
        <div className="auth__card" style={cardStyle}>
          {catalog.event && <b>{catalog.event.name}</b>}
          <p className="auth__hint storefront__voucher-ok">
            <Icon name="check" className="me-1" /> {t('seatClaimed')}
          </p>
          <button type="button" className="auth__primary" style={accent}
                  onClick={() => navigate('/member-home')}>
            {t('backToHome')}
          </button>
        </div>
      ) : empty ? (
        <div className="auth__card" style={cardStyle}>
          <p>{t('emptyCart')}</p>
          <Link to={`/${slug}`} className="auth__primary" style={accent}>
            {t('backToShop', { defaultValue: 'Back' })}
          </Link>
        </div>
      ) : pendingEmail ? (
        /* Invoice orders live in the buyer's mailbox until they confirm. Say so
           plainly, and say that nothing is booked yet — a screen that reads like a
           receipt would have them waiting for tickets that were never ordered. */
        <div className="auth__card" style={cardStyle}>
          <b>{t('confirmYourOrderTitle')}</b>
          <p className="auth__hint">{t('confirmYourOrderBody', { email: pendingEmail })}</p>
          <p className="auth__hint">{t('confirmYourOrderReserved')}</p>
          <Link to={`/${slug}`} className="auth__primary" style={accent}>
            {t('backToShop', { defaultValue: 'Back' })}
          </Link>
        </div>
      ) : capFull ? (
        /* The last team spots went while she was filling in the form, or her basket holds
           more teams than are left. Nothing was written; the waiting list is the way on. */
        <div className="auth__card" style={cardStyle}>
          <b>{t('teamsFullTitle')}</b>
          <p className="auth__hint">{t('teamCapReached')}</p>
          {capFull.waiting_list_url && (
            <a href={capFull.waiting_list_url} className="auth__primary" style={accent}>
              {t('joinWaitingList')}
            </a>
          )}
          <Link to={`/${slug}`} className="auth__ghost">
            {t('backToShop', { defaultValue: 'Back' })}
          </Link>
        </div>
      ) : nameClash ? (
        /* Two friends at one table both typing "Sabrina's team" is how an event
           ends up with four one-person teams. Ask before making the second one:
           joining hers costs the same and puts them in the same team. */
        <div className="auth__card" style={cardStyle}>
          <b>{t('teamNameExistsTitle')}</b>
          <p className="auth__hint">
            {t('teamNameExistsBody', { team: nameClash.team_name })}
          </p>
          {nameClash.captain_first_name && (
            <p className="auth__hint">
              {t('teamNameExistsCaptain', { name: nameClash.captain_first_name })}
            </p>
          )}
          {/* How to end up in HER team instead — the invite link, where she finds it,
              and that the seat is still paid for on that link. Without this the card
              refuses and offers no route to the outcome we actually want. */}
          <p className="auth__hint">
            {nameClash.captain_first_name
              ? t('teamNameExistsAskInvite', { name: nameClash.captain_first_name })
              : t('teamNameExistsAskInviteAnon')}
          </p>
          <Link to={`/${slug}`} className="auth__primary" style={accent}>
            {t('backToShop', { defaultValue: 'Back' })}
          </Link>
          <button type="button" className="auth__ghost"
                  onClick={() => { confirmNewTeam.current = true; setNameClash(null); buy(); }}>
            {t('createSeparateTeam')}
          </button>
        </div>
      ) : (
        <form className="auth__card" onSubmit={buy} style={cardStyle}>
          {/* A member with Lab tickets left: offered first, never forced — buying seats
              for colleagues on the same Lab stays possible underneath. */}
          {catalog.member_seat?.claimable && (
            <div className="storefront__summary">
              <p className="auth__hint">{t('useLabTicketHint')}</p>
              <button type="button" className="auth__primary" style={accent}
                      onClick={spendTicket} disabled={claiming}>
                {claiming ? <span className="auth__spinner" /> : <Icon name="ticket" />}
                {t('useLabTicket')}
              </button>
            </div>
          )}

          {/* What you're buying — VAT always split out as its own line */}
          {mode === 'first_seat' ? (
            <div className="storefront__summary">
              <div className="storefront__product-head">
                <span className="storefront__product-name">
                  {t('yourSeat')} — {seatProduct.name}
                </span>
                <span className="storefront__product-price">
                  {money(catalog.currency, seatNet)}
                </span>
              </div>
              {voucher && seatDiscount > 0 && (
                <div className="storefront__product-head">
                  <span>{t('voucherApplied')} ({voucher.code})</span>
                  <span>−{money(catalog.currency, seatDiscount)}</span>
                </div>
              )}
              {catalog.with_vat ? (
                <>
                  <div className="storefront__product-head">
                    <span>{t('vat')} ({seatVatRate}%)</span>
                    <span>{money(catalog.currency, seatNetAfter * seatVatRate / 100)}</span>
                  </div>
                  <div className="storefront__product-head storefront__total">
                    <span>{t('orderTotal')}</span>
                    <span>{money(catalog.currency, seatNetAfter * (1 + seatVatRate / 100))}</span>
                  </div>
                </>
              ) : (voucher && seatDiscount > 0 && (
                <div className="storefront__product-head storefront__total">
                  <span>{t('orderTotal')}</span>
                  <span>{money(catalog.currency, seatNetAfter)}</span>
                </div>
              ))}
              <p className="auth__hint">{t('firstSeatExplainer')}</p>
            </div>
          ) : (
            <div className="storefront__summary">
              {cartLines.map((p) => (
                <div key={p.rate_id} className="storefront__product-head">
                  <span className="storefront__product-name">
                    {cart[p.rate_id]}× {p.name}
                  </span>
                  <span className="storefront__product-price">
                    {money(catalog.currency, (p.effective_price ?? p.unit_price) * cart[p.rate_id])}
                  </span>
                </div>
              ))}
              <div className="storefront__product-head">
                <span>{t('subtotal')}</span>
                <span>{money(catalog.currency, totals.subtotal)}</span>
              </div>
              {voucher && voucher.discount > 0 && (
                <div className="storefront__product-head">
                  <span>{t('voucherApplied')} ({voucher.code})</span>
                  <span>−{money(catalog.currency, voucher.discount)}</span>
                </div>
              )}
              {catalog.with_vat && (
                <div className="storefront__product-head">
                  <span>{t('vat')}</span>
                  <span>{money(catalog.currency, cartVat)}</span>
                </div>
              )}
              <div className="storefront__product-head storefront__total">
                <span>{t('orderTotal')}</span>
                <span>{money(catalog.currency, cartTotal)}</span>
              </div>
            </div>
          )}

          {/* Voucher FIRST, with an Apply step, before any details are filled in — the buyer
              sees the discount up front. UX only; the order re-validates the code at checkout.
              Works for the full cart AND a single seat (first_seat) — a code the admin hasn't
              flagged for individual seats is refused server-side with a friendly message. */}
          <div className="storefront__voucher">
              <Field label={t('voucherCode')}>
                <div className="storefront__voucher-row">
                  <input className="auth__input" type="text" value={form.voucher_code}
                         onChange={onVoucherChange} placeholder={t('voucherCodePlaceholder')} />
                  <button type="button" className="storefront__voucher-apply" style={accent}
                          onClick={applyVoucher}
                          disabled={voucherLoading || !form.voucher_code.trim()}>
                    {voucherLoading ? <span className="auth__spinner" /> : t('applyVoucher')}
                  </button>
                </div>
              </Field>
              {voucher && (
                <p className="auth__hint storefront__voucher-ok">
                  <Icon name="check" className="me-1" />
                  {/* A community code is attribution, not a promotion: it normally carries
                      no discount at all, and "Voucher applied — −0.00" reads as a code that
                      did nothing. Name what it DID do, and who it was for. */}
                  {voucher.community
                    ? (voucher.discount > 0
                        ? t('voucherCommunityThanksDiscount', {
                            community: voucher.community.name,
                            amount: money(catalog.currency, voucher.discount) })
                        : t('voucherCommunityThanks', { community: voucher.community.name }))
                    : (voucher.discount > 0
                        ? `${t('voucherApplied')} — −${money(catalog.currency, voucher.discount)}`
                        : t('voucherAccepted'))}
                </p>
              )}
              {voucherError && (
                <p className="auth__hint storefront__voucher-err">
                  {t(voucherError.key, { count: voucherError.count, defaultValue: t('voucherInvalid') })}
                </p>
              )}
          </div>

          {mode === 'first_seat' && solo && (
            <p className="auth__hint">{t('soloCheckoutHint')}</p>
          )}
          {mode === 'first_seat' && !solo && (
            <>
              <Field label={t('teamName')}>
                <input className="auth__input" type="text" value={form.team_name}
                       onChange={set('team_name')} placeholder={t('teamNamePlaceholder')} />
              </Field>
              <p className="auth__hint">{t('teamNameChangeableLater')}</p>
            </>
          )}

          {/* Booking through a community leader's code: read-only, because nothing here
              is the buyer's to fill in. Her team stays her own — the community is a
              label on it, not its owner. */}
          {community && (
            <p className="auth__hint storefront__community-note">
              {t('bookingWithCommunity', { community: community.name })}
            </p>
          )}

          {/* B2B: invoice the company (tax id required). Works for a whole
              package AND a single seat the employer pays; the buyer is linked
              with the limited orderer role — never full company access. */}
          <label className="auth__hint d-flex align-items-start" style={{ gap: '0.5rem' }}>
            <input type="checkbox" className="form-check-input storefront__check"
                   checked={form.company_order} onChange={set('company_order')} />
            <span>{t('orderForCompany')}</span>
          </label>
          {form.company_order && (
            <>
              <Field label={t('companyName')}>
                <input className={cls('company_name')} type="text" value={form.company_name}
                       onChange={set('company_name')} autoComplete="organization" required />
              </Field>
              <Field label={taxIdLabel}>
                <input className={cls('company_vat')} type="text" value={form.company_vat}
                       onChange={set('company_vat')} placeholder={taxIdPlaceholder} required />
              </Field>
            </>
          )}

          {/* Logged in? Then SAY under which account the purchase runs —
              silently hiding the email field reads as a bug. */}
          {loggedIn && sessionUser && (
            <p className="auth__hint">
              🧑 {t('buyingAs', { email: sessionUser.email })}{' '}
              <button type="button" className="storefront__seat-link" onClick={logout}>
                {t('notYouLogout')}
              </button>
            </p>
          )}

          {!loggedIn && (
            <div className="auth__row">
              <Field label={t('firstName')}>
                <input className={cls('first_name')} type="text" value={form.first_name}
                       onChange={set('first_name')} autoComplete="given-name" required />
              </Field>
              <Field label={t('lastName')}>
                <input className={cls('last_name')} type="text" value={form.last_name}
                       onChange={set('last_name')} autoComplete="family-name" required />
              </Field>
            </div>
          )}

          {!loggedIn && (
            <Field label={t('email')}>
              <input className={cls('email')} type="email" value={form.email}
                     onChange={set('email')} autoComplete="email" required />
            </Field>
          )}
          {(!loggedIn || askMobile) && (
            <>
              <Field label={t('mobileNumber')}>
                <input className={cls('mobile')} type="tel" value={form.mobile}
                       onChange={set('mobile')} placeholder={t('enterMobile')}
                       autoComplete="tel" required />
              </Field>
              <WhatsAppNotice brandName={catalog.brand_name} />
            </>
          )}

          {/* The billing address is only asked when a COMPANY is invoiced — a person buying
              her own ticket gives a name, an email and a mobile, and pays. As little
              friction as possible in the order process (owner, 2026-09-29). */}
          {form.company_order && (
            <>
            <div className="auth__row">
              <Field label={t('street')}>
                <input className={cls('street')} type="text" value={form.street}
                       onChange={set('street')} autoComplete="address-line1" required />
              </Field>
              <Field label={t('number')}>
                <input className="auth__input" type="text" value={form.number}
                       onChange={set('number')} />
              </Field>
            </div>
            <div className="auth__row">
              <Field label={t('zip')}>
                <input className={cls('zip')} type="text" value={form.zip}
                       onChange={set('zip')} autoComplete="postal-code" required />
              </Field>
              <Field label={t('city')}>
                <input className={cls('city')} type="text" value={form.city}
                       onChange={set('city')} autoComplete="address-level2" required />
              </Field>
            </div>
            <Field label={t('country')}>
              <input className={cls('country')} type="text" value={form.country}
                     onChange={set('country')} autoComplete="country-name" required />
            </Field>
            </>
          )}

          {catalog.terms_url && (
            <label className={`auth__hint d-flex align-items-start${missing.has('terms_accepted') ? ' storefront__label--missing' : ''}`}
                   style={{ gap: '0.5rem' }}>
              <input type="checkbox" className="form-check-input storefront__check"
                     checked={form.terms_accepted}
                     onChange={set('terms_accepted')} required />
              <span>
                {t('iAcceptThe')}{' '}
                <a className="storefront__link" href={catalog.terms_url}
                   target="_blank" rel="noreferrer">
                  {t('termsAndConditions')}
                </a>
              </span>
            </label>
          )}

          <button type="submit" className="auth__primary" style={accent}
                  disabled={submitting}>
            {submitting ? <span className="auth__spinner" /> : <Icon name="ticket" />}
            {mode === 'first_seat' ? t('buyMySeat')
              : (catalog.allow_invoice ? t('payByCard') : t('placeOrder'))}
          </button>
          {/* The second way to pay, where the brand allows it. A UAE company runs on
              purchase order → tax invoice → transfer; a card page is a dead end for
              the buyer whose finance department will not use one. Named after what the
              buyer does (transfer the money), not after the paperwork we send them —
              `invoice` stays the wire-level name of the method. */}
          {catalog.allow_invoice && (
            <>
              <button type="button" className="auth__ghost" disabled={submitting}
                      onClick={(e) => buy(e, 'invoice')}>
                <Icon name="file-invoice" />
                {t('payByWireTransfer')}
              </button>
              <p className="auth__hint">{t('payByWireTransferHint')}</p>
            </>
          )}

          {/* "None of these fit" and "my procurement needs your trade licence" are
              both questions people have with the price list in front of them. */}
          <StorefrontHelp documents={catalog.documents} contact={catalog.contact}
                          brandName={catalog.brand_name}
                          eventName={catalog.event && catalog.event.name}
                          accentColor={catalog.primary_color} />

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default StorefrontCheckout;
